<template>
  <label :class="[rootClass, b(modifiers)]">
    <input
      v-model="internalValue"
      v-bind="$attrs"
      :class="b('field')"
      :aria-checked="isChecked ? 'true' : 'false'"
      :disabled="disabled || readonly"
      :value="value"
      :name="name"
      :readonly="readonly"
      :tabindex="readonly ? 0 : null"
      type="checkbox"
      @blur="onBlur"
      @focus="onFocus"
    />
    <span :class="b('indicator')"></span>
    <span :class="b('label-text')">
      <slot></slot>
    </span>
  </label>
</template>

<script>
  import designMixin from '@/mixins/design.js';
  import formStates from '@/mixins/form-states.js';

  /**
   * Checkbox component for form elements.
   * Can be used as single element with a Boolean value or multiple checkboxes with an Array.
   */
  export default {
    name: 'e-checkbox',

    mixins: [formStates, designMixin],
    inheritAttrs: false,

    props: {
      /**
       * v-model value.
       */
      modelValue: {
        type: [Boolean, Array],
        required: true,
      },

      /**
       * Sets the name of the checkbox.
       */
      name: {
        type: String,
        required: true,
      },

      /**
       * Sets the value of the checkbox.
       */
      value: {
        type: [String, Number, Boolean],
        default: true,
      },

      /**
       * Sets the disabled state of the checkbox.
       */
      disabled: {
        type: Boolean,
        default: false,
      },

      /**
       * Determines if the checkbox should not be editable (note, this is not supported natively).
       */
      readonly: {
        type: Boolean,
        default: false,
      },

      /**
       * Allows to set the desired layout variant.
       */
      variant: {
        type: String,
        default: 'default',
        validator(value) {
          return ['default', 'toggle', 'bullet'].includes(value);
        },
      },

      /**
       * Set the placement of the button. Only available in v2 design.
       */
      buttonPosition: {
        type: String,
        default: 'left',
        validator(value) {
          return ['left', 'right'].includes(value);
        },
      },

      /**
       * Sets the state to be indeterminate. (Only V2 design)
       */
      indeterminate: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['focus', 'blur', 'update:modelValue'],

    // data() {
    //   return {};
    // },

    computed: {
      modifiers() {
        return {
          variant: this.design === 'design2' ? 'default' : this.variant,
          readonly: this.readonly,
          buttonPosition: this.buttonPosition,
          stateIndeterminate: this.indeterminate,
          ...this.stateModifiers,
          ...this.designModifiers,
        };
      },

      /**
       * Sets/gets the components value.
       *
       * @returns {Boolean|Array} Status of the checkbox
       */
      internalValue: {
        get() {
          return this.modelValue;
        },
        set(value) {
          this.$emit('update:modelValue', value);
        },
      },

      /**
       * Evaluates if the checkbox is currently selected.
       *
       * @returns {Boolean} Return status.
       */
      isChecked() {
        return Array.isArray(this.modelValue) ? this.internalValue.includes(this.value) : this.internalValue;
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Emits focus to parent and wrapper component.
       * Update "hasFocus" state.
       */
      onFocus() {
        this.hasFocus = true;

        this.$emit('focus', this.hasFocus);
        this.$parent.$emit('focus');
      },

      /**
       * Emits blur to parent and wrapper component.
       * Update "hasFocus" state.
       */
      onBlur() {
        this.hasFocus = false;

        this.$emit('blur', this.hasFocus);
        this.$parent.$emit('blur');
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:math';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/extends' as *;
  @use '@/setup/scss/functions';
  @use '@/setup/scss-v2/component' as v2;
  @use '@/setup/scss-v2/fontawesome' as v2_fa;

  .e-checkbox {
    $this: &;

    position: relative;
    display: block;
    cursor: pointer;
    font-size: functions.get-theme-var('e-checkbox-font-size');
    line-height: functions.get-theme-var('e-checkbox-line-height');

    @include mixins.media(lg) {
      font-size: functions.get-theme-var('e-checkbox-font-size--lg');
    }

    &__field {
      position: absolute;
      left: -200vw;
      -webkit-appearance: none;
      appearance: none;
    }

    &__hint {
      @include mixins.font(variables.$font-size--12);

      display: block;
      color: variables.$color-grayscale--500;
    }

    &__notification {
      @include mixins.font(variables.$font-size--14);

      &:empty {
        display: none;
      }

      #{$this}--state-error & {
        color: variables.$color-status--danger;
      }

      #{$this}--state-warning & {
        color: variables.$color-status--warning;
      }

      #{$this}--state-info & {
        color: variables.$color-status--info;
      }

      #{$this}--state-success & {
        color: variables.$color-status--success;
      }
    }

    &__label-text {
      @extend %hyphens;

      color: functions.get-theme-var('e-checkbox-label-color');
    }

    &--state-error {
      #{$this}__label-text {
        color: functions.get-theme-var('e-checkbox-label-color--error');
      }

      &#{$this}--variant-default {
        #{$this}__indicator {
          border-color: functions.get-theme-var('e-checkbox-indicator-border-color--error');
          background: functions.get-theme-var('e-checkbox-indicator-background-color--error');
        }
      }
    }

    &--state-warning {
      #{$this}__label-text {
        color: variables.$color-status--warning;
      }

      #{$this}__checkbox {
        &::before {
          border-color: variables.$color-status--warning;
        }
      }

      &#{$this}--variant-default {
        #{$this}__indicator {
          border-color: functions.get-theme-var('e-checkbox-indicator-border-color--warning');
          background: functions.get-theme-var('e-checkbox-indicator-background-color--warning');
        }
      }
    }

    &--variant-default {
      display: flex;
      align-items: functions.get-theme-var('e-checkbox-align-items');

      #{$this}__indicator {
        position: relative;
        flex: 0 0 auto;
        width: functions.get-theme-var('e-checkbox-indicator-size');
        height: functions.get-theme-var('e-checkbox-indicator-size');
        border: 1px solid functions.get-theme-var('e-checkbox-indicator-border-color');
        border-radius: functions.get-theme-var('e-checkbox-indicator-border-radius');
        background: functions.get-theme-var('e-checkbox-indicator-background-color');
        transition-timing-function: ease-in-out;
        transition-duration: 200ms;
        transition-property: border, opacity;

        &::before {
          position: absolute;
          content: '';
          opacity: 0;
          background: transparent functions.get-theme-var('e-checkbox-indicator-icon') no-repeat center / 70%;
          transition: inherit;
          inset: 0;
        }
      }

      #{$this}__label-text {
        display: block; // Keep indent.
        margin: 0;
        padding-left: functions.rem(variables.$spacing--10);
      }

      #{$this}__field:checked {
        ~ #{$this}__indicator::before {
          opacity: 1;
        }

        ~ #{$this}__label-text {
          color: functions.get-theme-var('e-checkbox-label-color--checked');
        }
      }
    }

    &--variant-toggle {
      $e-toggle--size: 1rem;

      display: flex;
      align-items: center;
      cursor: pointer;

      #{$this}__indicator {
        position: relative;
        width: 1.5 * $e-toggle--size;
        height: math.div($e-toggle--size, 3) * 2;
        margin: 0 math.div($e-toggle--size, 2);
        border-radius: $e-toggle--size;
        background-color: variables.$color-grayscale--500;
        transition: background-color variables.$transition-duration--300;

        &::before {
          position: absolute;
          top: 50%;
          left: math.div($e-toggle--size, -2);
          content: '';
          width: $e-toggle--size;
          height: $e-toggle--size;
          border-radius: 50%;
          background-color: variables.$color-grayscale--1000;
          box-shadow: functions.rem(0 1px 2px 1px) rgba(variables.$color-grayscale--0, 0.2);
          transform: translateY(-50%);
          transition: left variables.$transition-duration--300;
        }
      }

      #{$this}__field {
        &:checked ~ #{$this}__indicator {
          background-color: rgba(variables.$color-primary--1, 0.3);

          &::before {
            left: calc(100% - (#{$e-toggle--size} / 2));
            background-color: variables.$color-primary--1;
            transform: translateY(-50%);
          }
        }
      }

      #{$this}__label-text {
        margin-left: $e-toggle--size;
      }
    }

    &--variant-bullet {
      @include mixins.font(variables.$font-size--18);

      $bullet-size: 30px;
      $bullet-icon-size: calc($bullet-size / 2);
      $bullet-icon-spacing: calc($bullet-icon-size / 2);

      display: flex;
      color: variables.$color-primary--1;
      font-weight: variables.$font-weight--semi-bold;

      #{$this}__indicator {
        display: block;
        flex: 0 0 auto;
        width: functions.rem($bullet-size);
        height: functions.rem($bullet-size);
        margin-right: functions.rem(variables.$spacing--30);
        border: 1px solid variables.$color-grayscale--800;
        border-radius: 50%;
      }

      #{$this}__field {
        &:checked ~ #{$this}__indicator {
          display: flex;
          justify-content: stretch;
          align-items: stretch;
          border-color: variables.$color-primary--1;
          background: variables.$color-primary--1;

          &::before {
            @include mixins.iconSprite('i-check', $color: variables.$color-grayscale--1000);

            position: absolute;
            content: '';
            width: functions.rem($bullet-icon-size);
            height: functions.rem($bullet-icon-size);
            top: functions.rem($bullet-icon-spacing);
            left: functions.rem($bullet-icon-spacing);
          }
        }
      }
    }

    &--disabled {
      cursor: default;
      color: functions.get-theme-var('e-checkbox-color--disabled');

      &#{$this}--variant-default {
        #{$this}__indicator {
          border-color: functions.get-theme-var('e-checkbox-indicator-border-color--disabled');
        }

        #{$this}__field:checked {
          & ~ #{$this}__indicator::before {
            background-image: functions.get-theme-var('e-checkbox-indicator-icon--disabled');
          }

          & ~ #{$this}__label-text {
            cursor: default;
            color: functions.get-theme-var('e-checkbox-label-color--disabled');
          }
        }
      }

      &#{$this}--variant-toggle {
        #{$this}__indicator {
          background-color: rgba(variables.$color-grayscale--500, 0.7);
        }

        #{$this}__field {
          &:checked ~ #{$this}__indicator {
            background-color: rgba(variables.$color-primary--1, 0.1);

            // stylelint-disable-next-line max-nesting-depth
            &::before {
              background-color: rgba(variables.$color-primary--1, 0.7);
            }
          }
        }
      }

      &#{$this}--variant-bullet {
        #{$this}__field {
          &:checked ~ #{$this}__indicator {
            border-color: transparent;
            background-color: rgba(variables.$color-primary--1, 0.7);
          }
        }
      }
    }

    &--readonly {
      pointer-events: none;
    }
  }

  //
  // Design V2
  //
  @include v2.define-theme-properties-for(
    'default',
    'e-checkbox',
    (
      'indicator-size--large': 24px,
      'indicator-size--medium': 20px,
      'indicator-size--small': 16px,
      'indicator-icon-size--large': 12px,
      'indicator-icon-size--medium': 8px,
      'indicator-icon-size--small': 6px,
      'indicator-inner-radius': 5px,
      'indicator-outer-radius': 10px,
      'indicator-inner-border-width': 2px,
      'indicator-border-width': 4px,
      'indicator-border-color': transparent,
      'indicator-border-color--hover': var(--color-gray--50),
      'indicator-border-color--error': var(--color-red--100),
      'indicator-border-color--warning': var(--color-yellow--100),
      'indicator-border-color--success': var(--color-green--100),
      'indicator-border-color--info': var(--color-blue--100),
      'indicator-color': var(--color-primary--1),
      'indicator-color--disabled': var(--color-gray--300),
      'label-margin--large': 10px,
      'label-margin--medium': 8px,
      'label-margin--small': 6px,
      'label-color': inherit,
      'label-color--checked': var(--color-primary--1),
      'label-color--disabled': var(--color-gray--300),
    )
  );

  .e-checkbox {
    $this: &;

    &--design-size-large {
      --label-margin: var(--e-checkbox-label-margin--large);
      --indicator-size: var(--e-checkbox-indicator-size--large);
      --indicator-icon-size: var(--e-checkbox-indicator-icon-size--large);

      #{$this}__label-text {
        @include v2.font-body--large();
      }
    }

    &--design-size-medium {
      --label-margin: var(--e-checkbox-label-margin--medium);
      --indicator-size: var(--e-checkbox-indicator-size--medium);
      --indicator-icon-size: var(--e-checkbox-indicator-icon-size--medium);

      #{$this}__label-text {
        @include v2.font-body--medium();
      }
    }

    &--design-size-small {
      --label-margin: var(--e-checkbox-label-margin--small);
      --indicator-size: var(--e-checkbox-indicator-size--small);
      --indicator-icon-size: var(--e-checkbox-indicator-icon-size--small);

      #{$this}__label-text {
        @include v2.font-body--small();
      }
    }

    &--button-position-right {
      flex-direction: row-reverse;
    }

    &--design-design2 {
      --transition: var(--duration-regular) ease-in-out;

      display: flex;
      gap: var(--label-margin);
      align-items: flex-start;

      #{$this}__label-text {
        @include v2.transition-all(color, var(--transition));

        display: block;
        padding-top: var(--e-checkbox-indicator-border-width);
        padding-left: 0;
        color: var(--e-checkbox-label-color);
      }

      #{$this}__indicator {
        @include v2.transition-all(border background, var(--transition));
        @include v2_fa.fa-icon-solid(v2_fa.$fa-var-check);

        display: block;
        width: auto;
        height: auto;
        border: var(--e-checkbox-indicator-border-width) solid var(--e-checkbox-indicator-border-color);
        border-color: var(--e-checkbox-indicator-border-color);
        border-radius: var(--e-checkbox-indicator-outer-radius);

        &::before {
          @include v2.transition-all(border color background, var(--transition));

          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          box-sizing: border-box;
          width: var(--indicator-size);
          height: var(--indicator-size);
          opacity: 1;
          border: var(--e-checkbox-indicator-inner-border-width) solid var(--e-checkbox-indicator-color);
          border-radius: var(--e-checkbox-indicator-inner-radius);
          background: none;
          color: transparent;
          font-size: var(--indicator-icon-size);
        }
      }

      #{$this}__field:checked {
        ~ #{$this}__indicator {
          border-color: var(--e-checkbox-indicator-border-color);

          &::before {
            background: var(--e-checkbox-indicator-color);
            color: var(--color-white);
          }
        }

        ~ #{$this}__label-text {
          color: var(--e-checkbox-label-color--checked);
        }
      }

      &#{$this}--disabled {
        cursor: default;

        #{$this}__indicator {
          border-color: var(--e-checkbox-indicator-border-color);
          background-color: transparent;

          &::before {
            border-color: var(--e-checkbox-indicator-color--disabled);
          }
        }

        #{$this}__label-text {
          cursor: default;
          color: var(--e-checkbox-label-color--disabled);
        }

        #{$this}__field:checked {
          & ~ #{$this}__indicator {
            background-color: var(--e-checkbox-indicator-color--disabled);

            /* stylelint-disable-next-line max-nesting-depth */
            &::before {
              background-color: var(--e-checkbox-indicator-color--disabled);
              background-image: none;
            }
          }

          & ~ #{$this}__label-text {
            color: var(--e-checkbox-label-color--disabled);
          }
        }
      }

      &#{$this}--state-indeterminate {
        &#{$this}--variant-default {
          #{$this}__indicator {
            @include v2_fa.fa-icon-solid(v2_fa.$fa-var-dash);
          }
        }
      }

      &#{$this}--state-error {
        &#{$this}--variant-default {
          #{$this}__indicator {
            border-color: var(--e-checkbox-indicator-border-color--error);
          }
        }
      }

      &#{$this}--state-warning {
        &#{$this}--variant-default {
          #{$this}__indicator {
            border-color: var(--e-checkbox-indicator-border-color--warning);
          }
        }
      }

      &#{$this}--state-success {
        &#{$this}--variant-default {
          #{$this}__indicator {
            border-color: var(--e-checkbox-indicator-border-color--success);
          }
        }
      }

      &#{$this}--state-info {
        &#{$this}--variant-default {
          #{$this}__indicator {
            border-color: var(--e-checkbox-indicator-border-color--info);
          }
        }
      }

      &:hover:not(#{$this}--disabled) {
        #{$this}__indicator {
          border-color: var(--e-checkbox-indicator-border-color--hover);
        }
      }
    }
  }
</style>
